import React, { useState, useEffect } from "react";
import { Loader2, UserCog } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@/app/store";
import {
  replaceEnrollmentAttendeeAction,
  replaceEnrollmentWithEmployeeAction,
} from "@/modules/eventRegistration/features/EventWaitlist/slices/enrollmentsWaitlistSlice";
import {
  EventEnrollmentItemResponseDTO,
  ReplacementType,
} from "@/modules/eventRegistration/features/EventWaitlist/api/fetchEventEnrollments/types";

interface ConfirmReplaceEnrollmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedRegistration: EventEnrollmentItemResponseDTO | null;
  eventUuid: string | undefined;
  onSuccess: () => Promise<void>;
}

export default function ConfirmReplaceEnrollmentModal({
  isOpen,
  onClose,
  selectedRegistration,
  eventUuid,
  onSuccess,
}: ConfirmReplaceEnrollmentModalProps) {
  const { toast } = useToast();
  const dispatch = useDispatch<AppDispatch>();

  const [searchTerm, setSearchTerm] = useState("");
  const [isReplacing, setIsReplacing] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newEmployee, setNewEmployee] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });

  useEffect(() => {
    if (isOpen) {
      setSearchTerm("");
      setShowNewForm(false);
      setNewEmployee({ firstName: "", lastName: "", email: "" });
    }
  }, [isOpen]);

  if (!selectedRegistration) return null;

  const replacements = selectedRegistration.replacements || [];
  const term = searchTerm.toLowerCase();
  const filtered = term
    ? replacements.filter(
        (emp) =>
          emp.firstName?.toLowerCase().includes(term) ||
          emp.lastName?.toLowerCase().includes(term) ||
          emp.workEmail?.toLowerCase().includes(term),
      )
    : replacements;

  const handleSelectEmployee = async (employee: ReplacementType) => {
    if (!eventUuid || isReplacing) return;
    setIsReplacing(true);
    try {
      await dispatch(
        replaceEnrollmentWithEmployeeAction({
          uuid: eventUuid,
          eventEnrollmentId: selectedRegistration.id,
          employeeId: employee.employeeId,
        }),
      );
      toast({
        title: "Replaced",
        description: `${employee.firstName} ${employee.lastName} is now enrolled.`,
      });
      onClose();
      await onSuccess();
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to replace",
        variant: "destructive",
      });
    } finally {
      setIsReplacing(false);
    }
  };

  const handleCreateAndReplace = async () => {
    if (!eventUuid || isReplacing) return;
    if (!newEmployee.firstName.trim() || !newEmployee.lastName.trim() || !newEmployee.email.trim()) {
      toast({
        title: "Error",
        description: "First name, last name, and email are required.",
        variant: "destructive",
      });
      return;
    }

    setIsReplacing(true);
    try {
      await dispatch(
        replaceEnrollmentAttendeeAction({
          uuid: eventUuid,
          eventEnrollmentId: selectedRegistration.id,
          newFirstName: newEmployee.firstName,
          newLastName: newEmployee.lastName,
          newEmail: newEmployee.email,
        }),
      );
      toast({
        title: "Replaced",
        description: `${newEmployee.firstName} ${newEmployee.lastName} is now enrolled.`,
      });
      onClose();
      await onSuccess();
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to replace",
        variant: "destructive",
      });
    } finally {
      setIsReplacing(false);
    }
  };

  const companyName = selectedRegistration.companyName || "the company";

  return (
    <Sheet open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-base font-normal text-gray-900 dark:text-white flex items-center gap-2">
            <UserCog className="h-4 w-4 text-gray-400" />
            Replace Employee
          </SheetTitle>
          <SheetDescription className="text-sm text-gray-500 dark:text-gray-400">
            Replace{" "}
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {selectedRegistration.firstName} {selectedRegistration.lastName}
            </span>{" "}
            with another employee from {companyName}.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {/* Search */}
          <Input
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search employees..."
            value={searchTerm}
          />

          {/* Employee list */}
          <div className="space-y-1 max-h-[300px] overflow-y-auto">
            {filtered.length === 0 && !searchTerm ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                No other employees found for {companyName}.
              </p>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                No matches for "{searchTerm}"
              </p>
            ) : (
              filtered.map((emp) => (
                <button
                  key={emp.employeeId}
                  className="w-full flex items-center justify-between py-2.5 px-3 rounded-md hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors text-left"
                  disabled={isReplacing}
                  onClick={() => handleSelectEmployee(emp)}
                  type="button"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {emp.firstName} {emp.lastName}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {emp.workEmail || "No email"}
                    </p>
                  </div>
                  <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0 ml-3">
                    Select
                  </span>
                </button>
              ))
            )}
          </div>

          {/* Add new employee */}
          {!showNewForm ? (
            <button
              className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
              onClick={() => setShowNewForm(true)}
              type="button"
            >
              Not in the system? Add a new employee →
            </button>
          ) : (
            <div className="border border-gray-200 dark:border-slate-700 rounded-md p-3 space-y-3">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                New Employee
              </p>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  disabled={isReplacing}
                  onChange={(e) =>
                    setNewEmployee((p) => ({ ...p, firstName: e.target.value }))
                  }
                  placeholder="First name"
                  value={newEmployee.firstName}
                />
                <Input
                  disabled={isReplacing}
                  onChange={(e) =>
                    setNewEmployee((p) => ({ ...p, lastName: e.target.value }))
                  }
                  placeholder="Last name"
                  value={newEmployee.lastName}
                />
              </div>
              <Input
                disabled={isReplacing}
                onChange={(e) =>
                  setNewEmployee((p) => ({ ...p, email: e.target.value }))
                }
                placeholder="Email"
                type="email"
                value={newEmployee.email}
              />
              <div className="flex justify-end gap-2">
                <Button
                  disabled={isReplacing}
                  onClick={() => setShowNewForm(false)}
                  size="sm"
                  variant="ghost"
                >
                  Cancel
                </Button>
                <Button
                  disabled={isReplacing}
                  onClick={handleCreateAndReplace}
                  size="sm"
                >
                  {isReplacing ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      Replacing...
                    </>
                  ) : (
                    "Add & Replace"
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
